/**
 * SEO helper — directly manipulates document.head because react-helmet-async v2
 * has known issues with meta-tag and title updates in our SPA setup.
 *
 * Usage:
 *   useEffect(() => {
 *     const cleanup = applySEO({
 *       title: 'My Page',
 *       description: '...',
 *       canonical: 'https://petfolk.in/path',
 *       ogType: 'article',
 *       ogImage: '...',
 *       articlePublishedTime: '2026-...',
 *       articleSection: 'Pet Care',
 *       articleTags: ['dog', 'grooming'],
 *       author: 'Petfolk',
 *       jsonLd: { ... },          // optional structured data
 *       jsonLdId: 'blog-jsonld',  // unique id for cleanup
 *     });
 *     return cleanup;
 *   }, [deps]);
 */

const MANAGED_ATTR = 'data-petfolk-seo';

function setMetaByProperty(property, content) {
  if (content === undefined || content === null) return;
  let m = document.querySelector(`meta[property="${property}"][${MANAGED_ATTR}="1"]`);
  if (!m) {
    // Remove any existing static (un-managed) tag with the same property to avoid duplicates
    document.querySelectorAll(`meta[property="${property}"]`).forEach(el => {
      if (!el.hasAttribute(MANAGED_ATTR)) el.remove();
    });
    m = document.createElement('meta');
    m.setAttribute('property', property);
    m.setAttribute(MANAGED_ATTR, '1');
    document.head.appendChild(m);
  }
  m.setAttribute('content', content);
}

function setMetaByName(name, content) {
  if (content === undefined || content === null) return;
  let m = document.querySelector(`meta[name="${name}"][${MANAGED_ATTR}="1"]`);
  if (!m) {
    document.querySelectorAll(`meta[name="${name}"]`).forEach(el => {
      if (!el.hasAttribute(MANAGED_ATTR)) el.remove();
    });
    m = document.createElement('meta');
    m.setAttribute('name', name);
    m.setAttribute(MANAGED_ATTR, '1');
    document.head.appendChild(m);
  }
  m.setAttribute('content', content);
}

function setCanonical(href) {
  if (!href) return;
  let l = document.querySelector(`link[rel="canonical"][${MANAGED_ATTR}="1"]`);
  if (!l) {
    document.querySelectorAll('link[rel="canonical"]').forEach(el => {
      if (!el.hasAttribute(MANAGED_ATTR)) el.remove();
    });
    l = document.createElement('link');
    l.setAttribute('rel', 'canonical');
    l.setAttribute(MANAGED_ATTR, '1');
    document.head.appendChild(l);
  }
  l.setAttribute('href', href);
}

function clearArticleTags() {
  document.querySelectorAll(`meta[property="article:tag"][${MANAGED_ATTR}="1"]`).forEach(el => el.remove());
}

function setJsonLd(id, data) {
  if (!id || !data) return;
  document.querySelectorAll(`script[type="application/ld+json"]#${CSS.escape(id)}`).forEach(el => el.remove());
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.id = id;
  s.setAttribute(MANAGED_ATTR, '1');
  s.text = JSON.stringify(data);
  document.head.appendChild(s);
}

export function applySEO({
  title,
  description,
  canonical,
  ogType = 'website',
  ogImage,
  ogSiteName = 'Petfolk',
  twitterCard = 'summary_large_image',
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleTags = [],
  author,
  jsonLd,
  jsonLdId,
}) {
  if (title) document.title = title;
  setMetaByName('description', description);
  setCanonical(canonical);

  setMetaByProperty('og:title', title);
  setMetaByProperty('og:description', description);
  setMetaByProperty('og:type', ogType);
  setMetaByProperty('og:url', canonical);
  setMetaByProperty('og:site_name', ogSiteName);
  if (ogImage) setMetaByProperty('og:image', ogImage);

  setMetaByName('twitter:card', twitterCard);
  setMetaByName('twitter:title', title);
  setMetaByName('twitter:description', description);
  if (ogImage) setMetaByName('twitter:image', ogImage);

  if (author) setMetaByName('author', author);

  // article:* tags
  if (ogType === 'article') {
    setMetaByProperty('article:published_time', articlePublishedTime);
    setMetaByProperty('article:modified_time', articleModifiedTime);
    setMetaByProperty('article:section', articleSection);
    clearArticleTags();
    (articleTags || []).forEach(t => {
      const m = document.createElement('meta');
      m.setAttribute('property', 'article:tag');
      m.setAttribute(MANAGED_ATTR, '1');
      m.setAttribute('content', t);
      document.head.appendChild(m);
    });
  }

  if (jsonLd && jsonLdId) setJsonLd(jsonLdId, jsonLd);

  // Cleanup function — restore default OG/Twitter when leaving page
  return () => {
    if (jsonLdId) {
      const s = document.getElementById(jsonLdId);
      if (s) s.remove();
    }
    clearArticleTags();
  };
}
